// ═══════════════════════════════════════════════════════════════════════════
// PAGE: GLOBAL ACTIONS LIST — entry (lot B.9/2 — découpage du monstre)
// Migration S5.2 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// Découpage en 2 sous-fichiers depuis le lot B.9 (mai 2026) :
//   - index.ts (ici)    : pages.actions (entry) + checklist modal + dispatch
//                         vers renderActionsPage. Charge les référentiels en
//                         parallèle puis délègue.
//   - render-page.ts    : renderActionsPage (orchestration de la liste, table,
//                         filtres, tri, persistence URL, export). Monolithique
//                         car toute la logique partage un état closure.

import { renderActionsPage } from './render-page.js';

interface ActionsProgram {
  id: number;
  name?: string;
  code?: string | null;
}

interface ActionsProject {
  id: number;
  name?: string;
  code?: string | null;
}

interface ActionsCommittee {
  id: number;
  code: string;
  name: string;
}

interface ActionsStructure {
  id: number;
  code: string;
  label: string;
  is_active?: boolean;
  parent_ids?: number[];
}

interface ActionsTemplate {
  id: number;
  name: string;
  actions: unknown[];
}

interface ActionsPhase {
  id: number;
  code: string;
  title: string;
  plan_id: number;
  plan_code: string;
  plan_title: string;
  plan_active?: boolean;
}

interface ActionsAppData {
  id: number;
  total?: number;
  instances?: unknown[];
}

// window.showMeetingWizard, window.showBasculeWizard et
// window.showImportActionsWizard sont typés dans leurs modules respectifs
// (page-meeting-wizard.ts, page-bascule.ts, page-import-actions-wizard.ts).

// Facettes serveur qu'un atterrissage de tuile peut poser dans le hash.
// Liste FERMÉE : une clé inconnue n'est jamais transmise au backend, pour que
// le hash ne devienne pas une surface d'injection de filtre.
const WORKSPACE_FACET_KEYS = [
  'person_id',
  'role',
  'status_id',
  'committee_id',
  'project_id',
  'risk_id',
  'mine',
  'late_only',
  'due_from',
  'due_to',
  'seen_state',
  'unassigned_team',
  'with_teams',
  'no_deadline',
  'no_status',
  'no_named_performer',
  'include_completed',
] as const;

pages.actions = async function () {
  setHeader(t('vocabulary.action_plural'), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  const uid = await ensureCurrentUserId();
  if (!uid) {
    setContent(`<div class="error">${esc(t('pageActions.notAuth'))}</div>`);
    return;
  }
  // Icône "téléchargement" (flèche vers le bas + barre de réception). Inline
  // `done=1` dans l'URL ⇔ « afficher les terminées » coché. Par défaut la liste
  // ne charge QUE les actions en cours (include_completed=false) — le toggle
  // re-dispatch la page avec done=1 pour re-fetcher avec les terminées.
  const _hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
  const _showDone = _hashParams.get('done') === '1';
  // Mode rattachement réunion (`?meeting=<id>`) : il faut charger AUSSI les
  // actions terminées, sinon une action principale clôturée reste cochée mais
  // invisible — l'utilisateur ne peut pas la décocher et elle « revient »
  // après enregistrement (bug #5). On force donc include_completed dans ce mode.
  const _isLinkMode = /^\d+$/.test(_hashParams.get('meeting') || '');
  // Facettes serveur venues d'un atterrissage de tuile Workspace. On ne
  // reconstruit rien : le serveur a publié ces clés dans `landing`, la page
  // les rejoue telles quelles. Toute autre clé du hash est ignorée.
  const _facets: Record<string, string> = {};
  for (const key of WORKSPACE_FACET_KEYS) {
    const v = _hashParams.get(key);
    if (v !== null && v !== '') _facets[key] = v;
  }
  const _fromWorkspace = Object.keys(_facets).length > 0;

  // SVG pour rester lisible quelle que soit la police, et éviter une dépendance
  // à une lib d'icônes. currentColor → s'adapte au theme du bouton.
  const dlIcon = `<svg aria-hidden="true" width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:6px;flex-shrink:0"><path d="M10 4v9"/><path d="M6 9l4 4 4-4"/><path d="M4 17h12"/></svg>`;
  // Icône "import" (flèche vers le haut + barre). Pendant de dlIcon, flèche
  // inversée pour signifier l'envoi d'un fichier vers l'application.
  const ulIcon = `<svg aria-hidden="true" width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:6px;flex-shrink:0"><path d="M10 13V4"/><path d="M6 8l4-4 4 4"/><path d="M4 17h12"/></svg>`;
  // Icône "matrice" (grille 2×2) pour le CTA Matrice Eisenhower.
  const matrixIcon = `<svg aria-hidden="true" width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:6px;flex-shrink:0"><rect x="3" y="3" width="14" height="14" rx="1"/><path d="M10 3v14M3 10h14"/></svg>`;
  // CTA secondaire "Matrice Eisenhower" : masqué si l'user n'a pas la perm
  // dédiée actions.matrix. Positionné juste avant le split-button "Ajouter".
  // C'est un TOGGLE liste ↔ matrice (câblage dans render-page.ts via
  // _wireMatrixToggle ; le libellé bascule sur #btn-actions-matrix-label).
  const matrixBtn =
    window.hasPermission && window.hasPermission('actions.matrix')
      ? `<button class="btn btn-secondary" id="btn-actions-matrix" type="button" aria-pressed="false" title="${esc(t('pageActions.btn.matrixTitle'))}" style="margin-right:8px;display:inline-flex;align-items:center">${matrixIcon}<span id="btn-actions-matrix-label">${esc(t('pageActions.btn.matrix'))}</span></button>`
      : '';
  setHeader(
    // Filtre venu d'une tuile : le dire, et offrir d'en sortir. Un utilisateur
    // qui arrive sur une liste restreinte sans savoir pourquoi conclut que
    // l'application a perdu ses actions.
    t('vocabulary.action_plural') +
      (_fromWorkspace
        ? ` <a href="#actions" class="ws-filter-chip" title="${esc(t('workspace.filterClear'))}">${esc(t('workspace.filterFrom'))} ×</a>`
        : ''),
    // Export reste séparé (action de sortie, pas de création).
    `<button class="btn btn-secondary ml-auto" id="btn-export-actions" aria-haspopup="menu" style="margin-right:8px;display:inline-flex;align-items:center">${dlIcon}${esc(t('pageActions.btn.export'))} ▾</button>` +
      matrixBtn +
      // Split-button "Ajouter ▾" (RefactoUX/Actions mai 2026) : regroupe les
      // 4 modes de création/import (Ajouter ad hoc, Analyser un CR, Checklist,
      // Importer XLS) dans un seul CTA. Largeur 160 px sur le bouton principal
      // pour absorber le libellé du plus long item du menu ("Analyser un CR").
      `<div style="position:relative;display:inline-flex;align-items:stretch">
         <button id="btn-new-action" class="btn btn-primary" type="button" style="width:160px;border-top-right-radius:0;border-bottom-right-radius:0;margin-right:1px">${esc(t('pageActions.btn.add'))}</button>
         <button id="btn-new-action-menu" class="btn btn-primary" type="button" aria-haspopup="menu" aria-expanded="false" title="${esc(t('pageActions.btn.addMenuTitle'))}" aria-label="${esc(t('pageActions.btn.addMenuTitle'))}" style="width:32px;border-top-left-radius:0;border-bottom-left-radius:0;padding:0">▾</button>
         <div id="new-action-menu" role="menu" style="display:none;position:absolute;top:calc(100% + 4px);right:0;background:var(--bg2);border:1px solid var(--border2);border-radius:var(--radius);box-shadow:0 8px 24px rgba(0,0,0,.15);width:193px;z-index:1000;overflow:hidden">
           <button id="menu-add-meeting" class="add-menu-item" type="button" role="menuitem" style="display:block;width:100%;text-align:left;padding:8px 12px;border:none;cursor:pointer;font-size:12px;color:var(--text)">${esc(t('pageActions.btn.meeting'))}</button>
           <button id="menu-add-checklist" class="add-menu-item" type="button" role="menuitem" style="display:block;width:100%;text-align:left;padding:8px 12px;border:none;cursor:pointer;font-size:12px;color:var(--text)">${esc(t('pageActions.btn.checklist'))}</button>
           <button id="menu-add-import" class="add-menu-item" type="button" role="menuitem" style="display:flex;align-items:center;width:100%;text-align:left;padding:8px 12px;border:none;cursor:pointer;font-size:12px;color:var(--text)">${ulIcon}${esc(t('pageActions.btn.import'))}</button>
         </div>
       </div>`,
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const [actions, users, statuses, programs, projects, committees, structures, phases] =
      (await Promise.all([
        api.getAllActions({
          includeCompleted: _facets.include_completed
            ? _facets.include_completed !== 'false'
            : _showDone || _isLinkMode,
          facets: _facets,
        }),
        loadUsers(),
        loadActionStatuses(),
        api.getPrograms(),
        api.getProjects(),
        loadCommittees().catch(() => []),
        (api.getStructuresForFilter() as Promise<ActionsStructure[]>).catch(() => []),
        (api.getPhasesForFilter() as Promise<ActionsPhase[]>).catch(() => []),
      ])) as unknown as [
        unknown[],
        unknown[],
        unknown[],
        ActionsProgram[],
        ActionsProject[],
        ActionsCommittee[],
        ActionsStructure[],
        ActionsPhase[],
      ];
    // Seules les structures ACTIVES sont proposées au filtre organisation.
    const activeStructures = (structures || []).filter((s) => s.is_active !== false);
    // Seules les phases de plans ACTIFS sont proposées au filtre plans d'action.
    const activePhases = (phases || []).filter((p) => p.plan_active !== false);
    renderActionsPage(
      actions,
      users,
      statuses,
      programs,
      projects,
      committees,
      activeStructures,
      activePhases,
    );
    // Masquage des items du menu "Ajouter ▾" selon les permissions de
    // l'utilisateur (RefactoUX/Actions mai 2026, héritage du masquage par
    // bouton individuel d'avant). Si les 3 items sont masqués, on cache aussi
    // le chevron pour ne pas laisser un dropdown vide.
    if (!(window.hasPermission && window.hasPermission('meetings.analyze'))) {
      const b = document.getElementById('menu-add-meeting');
      if (b) b.style.display = 'none';
    }
    if (!(window.hasPermission && window.hasPermission('checklists.read'))) {
      const b = document.getElementById('menu-add-checklist');
      if (b) b.style.display = 'none';
    }
    if (!(window.hasPermission && window.hasPermission('actions.import'))) {
      const b = document.getElementById('menu-add-import');
      if (b) b.style.display = 'none';
    }
    const visibleMenuItems = (
      ['menu-add-meeting', 'menu-add-checklist', 'menu-add-import'] as const
    ).filter((id) => {
      const el = document.getElementById(id);
      return el && el.style.display !== 'none';
    });
    if (visibleMenuItems.length === 0) {
      const chevron = document.getElementById('btn-new-action-menu');
      if (chevron) chevron.style.display = 'none';
      const addBtn = document.getElementById('btn-new-action');
      // Le seul mode restant = "Ajouter" → on remet les radius arrondis sur
      // ses 4 coins puisque le chevron a disparu.
      if (addBtn) {
        addBtn.style.borderTopRightRadius = '';
        addBtn.style.borderBottomRightRadius = '';
        addBtn.style.marginRight = '';
      }
    }

    // Wiring du dropdown "Ajouter ▾". Toggle au clic sur le chevron, fermeture
    // sur clic extérieur ou ESC. Chaque item ferme le menu avant d'exécuter
    // son action.
    const addMenuBtn = document.getElementById('btn-new-action-menu') as HTMLButtonElement | null;
    const addMenu = document.getElementById('new-action-menu');
    const closeAddMenu = (): void => {
      if (addMenu) addMenu.style.display = 'none';
      addMenuBtn?.setAttribute('aria-expanded', 'false');
      document.removeEventListener('mousedown', onOutsideAddMenu, true);
      document.removeEventListener('keydown', onEscAddMenu, true);
    };
    const onOutsideAddMenu = (e: MouseEvent): void => {
      const target = e.target as Node | null;
      if (!target) return;
      if (addMenu?.contains(target) || addMenuBtn?.contains(target)) return;
      closeAddMenu();
    };
    const onEscAddMenu = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeAddMenu();
    };
    addMenuBtn?.addEventListener('click', (e: Event) => {
      e.stopPropagation();
      if (!addMenu) return;
      const willOpen = addMenu.style.display === 'none';
      addMenu.style.display = willOpen ? 'block' : 'none';
      addMenuBtn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      if (willOpen) {
        setTimeout(() => {
          document.addEventListener('mousedown', onOutsideAddMenu, true);
          document.addEventListener('keydown', onEscAddMenu, true);
        }, 0);
      } else {
        document.removeEventListener('mousedown', onOutsideAddMenu, true);
        document.removeEventListener('keydown', onEscAddMenu, true);
      }
    });

    const meetingBtn = document.getElementById('menu-add-meeting');
    meetingBtn?.addEventListener('click', () => {
      closeAddMenu();
      if (typeof window.showMeetingWizard === 'function') {
        void window.showMeetingWizard();
      } else {
        toast(t('pageActions.toast.meetingMissing'), 'error');
      }
    });
    const importBtn = document.getElementById('menu-add-import');
    importBtn?.addEventListener('click', () => {
      closeAddMenu();
      if (typeof window.showImportActionsWizard === 'function') {
        void window.showImportActionsWizard();
      } else {
        toast(t('pageActions.toast.importMissing'), 'error');
      }
    });
    const checklistBtn = document.getElementById('menu-add-checklist') as HTMLButtonElement | null;
    checklistBtn?.addEventListener('click', () => {
      closeAddMenu();
      if (!checklistBtn) return;
      void api.withButton(checklistBtn, async () => {
        const templates = (await api.getTemplates()) as ActionsTemplate[];
        if (!templates.length) {
          toast(t('pageActions.toast.noTemplate'), 'error');
          return;
        }
        const projOpts = projects
          .map(
            (p) =>
              `<option value="${p.id}">${esc(p.code ? p.code + ' — ' : '')}${esc(p.name || '')}</option>`,
          )
          .join('');
        const tplOpts = templates
          .map(
            (tpl) =>
              `<option value="${tpl.id}">${t('pageActions.checklist.option', { name: tpl.name, n: tpl.actions.length })}</option>`,
          )
          .join('');
        openModal(
          t('pageActions.checklist.title'),
          `
        <div class="form-group"><label>${esc(t('pageActions.checklist.project'))}</label>
          <select id="cl-proj" style="width:100%"><option value="">${esc(t('pageActions.checklist.placeholder'))}</option>${projOpts}</select>
        </div>
        <div class="form-group mt-3"><label>${esc(t('pageActions.checklist.template'))}</label>
          <select id="cl-tpl" style="width:100%"><option value="">${esc(t('pageActions.checklist.placeholder'))}</option>${tplOpts}</select>
        </div>
        <p class="text-muted mt-2" style="font-size:12px">${esc(t('pageActions.checklist.help'))}</p>`,
          async () => {
            const projEl = document.getElementById('cl-proj') as HTMLSelectElement | null;
            const tplEl = document.getElementById('cl-tpl') as HTMLSelectElement | null;
            const pid = parseInt(projEl?.value || '0');
            const tid = parseInt(tplEl?.value || '0');
            if (!pid) {
              toast(t('pageActions.checklist.errProject'), 'error');
              return;
            }
            if (!tid) {
              toast(t('pageActions.checklist.errChecklist'), 'error');
              return;
            }
            let app: ActionsAppData | undefined;
            try {
              app = (await api.applyTemplate(pid, { template_id: tid })) as ActionsAppData;
            } catch (err: unknown) {
              const m = err instanceof Error ? err.message : String(err);
              toast(t('pageActions.checklist.errCreate', { msg: m }), 'error');
              return;
            }
            closeModal();
            toast(t('pageActions.checklist.toastCreated', { n: app?.total ?? '?' }));
            const targetHash = '#actions?all=1';
            if (window.location.hash === targetHash) {
              router.dispatch();
            } else {
              window.location.hash = targetHash;
            }
            let appData: ActionsAppData | undefined;
            let allUsers: unknown[] = [];
            let allStatuses: unknown[] = [];
            let allProjects: ActionsProject[] = [];
            try {
              [appData, allUsers, allStatuses, allProjects] = (await Promise.all([
                api.getApplication(app.id),
                loadUsers(),
                loadActionStatuses(),
                api.getProjects(),
              ])) as unknown as [ActionsAppData, unknown[], unknown[], ActionsProject[]];
            } catch (err: unknown) {
              const m = err instanceof Error ? err.message : String(err);
              toast(t('pageActions.checklist.errStep2', { msg: m }), 'error');
              return;
            }
            if (!appData || !Array.isArray(appData.instances)) {
              toast(t('pageActions.checklist.errInstances'), 'error');
              return;
            }
            if (typeof window.showBasculeWizard === 'function') {
              setTimeout(() => {
                void window.showBasculeWizard?.(
                  appData as never,
                  allUsers as never,
                  allStatuses as never,
                  allProjects as never,
                );
                const obs = new MutationObserver(() => {
                  if (!document.getElementById('modal-overlay')) {
                    obs.disconnect();
                    router.dispatch();
                  }
                });
                obs.observe(document.body, { childList: true });
              }, 0);
            } else {
              toast(t('pageActions.checklist.errBascule'), 'error');
            }
          },
          t('pageActions.checklist.btnNext'),
        );
      });
    });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

export {};
